import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';

import {
    useCreateDocumentsMutation,
    useGetConventionsQuery,
    useGetDistributorCompanyQuery,
    useGetDocumentAuditsQuery,
    useGetDocumentsQuery,
    useGetInvestmentFundsQuery,
    useReplaceDocumentMutation,
    useUpdateDistributorCompanyMutation,
} from 'api/main';
import { userSelectors } from 'reducers';
import { useDistributorDocuments, useUserRoleMatchCategory } from 'utils/hooks';
import { conventionStatus, docTypes, roleCategories } from 'utils/constants';
import { createDocumentsIRI, documentSorter, jsonParseSafe } from 'utils/helpers';
import { texts } from 'utils/languages/fr';

import Document from 'containers/subscriptions/Document';
import UploadDocument from 'containers/subscriptions/UploadDocument';
import { DocumentUpload } from 'containers/tunnel/common';
import { ActionButton, CustomSelect, DataTile, ListContainer, Loader } from 'components';
import { Typography, Box, MenuItem } from '@mui/material';

const documentsTileSx = theme => ({
    flexDirection: 'column',
    alignItems: 'flex-start',
    margin: 'auto',
    padding: '30px 60px',
    [theme.breakpoints.down('sm')]: {
        padding: '10px 30px',
    },
});

const DistributorDocuments = () => {
    const subscription = useSelector(userSelectors.getCurrentSubscription);
    const internalUser = useSelector(userSelectors.getInternalUser);
    const { data: distributorCompany, isLoading: companyLoading } = useGetDistributorCompanyQuery(
        subscription?.distributorCompany?.id,
        { skip: !subscription?.distributorCompany?.id }
    );
    const { documents, loading, refetch } = useDistributorDocuments(
        subscription?.distributorCompany,
        distributorCompany?.user?.id
    );
    const userIsAdminDistributor = useUserRoleMatchCategory(roleCategories.ADMIN_DISTRIBUTORS);
    const { data: conventions = [], isFetching: conventionsLoader } = useGetConventionsQuery({
        status: [conventionStatus.FULLY_SIGNED, conventionStatus.PARTIALLY_SIGNED],
    });
    const {
        data: documentAudits = [],
        isFetching: documentAuditsLoader,
        refetch: refetchDocumentAudits,
    } = useGetDocumentAuditsQuery(
        { belongsToId: internalUser?.id, pagination: false },
        { skip: !userIsAdminDistributor }
    );
    const { data: invoices = [], isFetching: invoiceLoader } = useGetDocumentsQuery({
        type: [docTypes.DISTRIBUTOR_BILL, docTypes.DISTRIBUTOR_BILL_EXCEL],
        pagination: false,
    });
    const { data: fundList = [], isLoading: fundsLoader } = useGetInvestmentFundsQuery({
        visible: 1,
    });
    const [updateDistributorCompany] = useUpdateDistributorCompanyMutation();
    const [
        createDocuments,
        { isLoading: createLoading, isSuccess: createSuccess, data: createResponse },
    ] = useCreateDocumentsMutation();
    const [replaceDocument, { isLoading: replaceLoading }] = useReplaceDocumentMutation();

    const commonDocs = useMemo(
        () =>
            documents.filter(
                d =>
                    d.type !== docTypes.DISTRIBUTOR_BILL &&
                    d.type !== docTypes.DISTRIBUTOR_BILL_EXCEL
            ),
        [documents]
    );
    const fundsFromInvoices = useMemo(() => {
        const set = new Set();
        invoices.forEach(inv => set.add(+jsonParseSafe(inv.subtype)?.investmentFund));
        return Array.from(set);
    }, [invoices]);
    const filteredFunds = useMemo(
        () => fundList.filter(f => fundsFromInvoices.includes(f.id)),
        [fundsFromInvoices, fundList]
    );

    const [logoFile, setLogoFile] = useState(internalUser?.distributorCompany?.companyLogo);
    const [selectedFund, setSelectedFund] = useState('');

    const handleLogoChange = useCallback(({ target: { files } }) => {
        const file = files[0];
        setLogoFile(file);
    }, []);

    const handleRemoveLogo = useCallback(() => {
        setLogoFile(null);
    }, []);

    const handleLogoUpload = useCallback(() => {
        const formData = new FormData();
        if (internalUser?.distributorCompany?.companyLogo) {
            formData.append('file', logoFile);
            replaceDocument({
                id: internalUser.distributorCompany.companyLogo.id,
                formData,
                updateUser: true,
            });
        } else {
            formData.append('files[]', logoFile);
            formData.append('type', docTypes.COMPANY);
            formData.append('subtype', 'logo');
            createDocuments({ formData });
        }
    }, [logoFile, createDocuments, replaceDocument, internalUser?.distributorCompany]);

    const handleDocumentChange = useCallback(
        callback => {
            refetchDocumentAudits();
            refetch();
            callback();
        },
        [refetch, refetchDocumentAudits]
    );

    useEffect(() => {
        if (createSuccess) {
            updateDistributorCompany({
                payload: {
                    id: internalUser.distributorCompany.id,
                    companyLogo: createDocumentsIRI(createResponse[0].id),
                },
            });
        }
    }, [createResponse, createSuccess, internalUser, updateDistributorCompany]);

    useEffect(() => {
        if (filteredFunds.length) {
            setSelectedFund(filteredFunds?.[0]?.id);
        }
    }, [filteredFunds]);

    return (
        <ListContainer>
            <Box>
                <Typography variant="h5" align="left" gutterBottom>
                    {texts.MY_DOCUMENTS}
                </Typography>
            </Box>
            <Loader
                loading={loading || companyLoading || fundsLoader || invoiceLoader}
                marginTop={0}
                paddingY={10}
                forceStop={!loading && !companyLoading && !fundsLoader}
            >
                {invoices.length > 0 && (
                    <DataTile title={texts.INVOICES} sx={documentsTileSx}>
                        <CustomSelect
                            value={selectedFund}
                            onChange={e => setSelectedFund(e.target.value)}
                        >
                            {filteredFunds.map(f => (
                                <MenuItem key={f.id} value={f.id}>
                                    {f.name}
                                </MenuItem>
                            ))}
                        </CustomSelect>
                        {invoices
                            .filter(
                                inv => +jsonParseSafe(inv.subtype).investmentFund === +selectedFund
                            )
                            .sort(documentSorter)
                            .map(doc => (
                                <Box
                                    key={doc.id}
                                    display="flex"
                                    flexWrap="wrap"
                                    width="100%"
                                    paddingX={1}
                                >
                                    <Document
                                        fullName={doc.name.replace(/\.[^.]+$/, '')}
                                        doc={doc}
                                        onChange={handleDocumentChange}
                                    />
                                </Box>
                            ))}
                    </DataTile>
                )}
            </Loader>
            <Loader loading={conventionsLoader} marginTop={10} forceStop={!conventionsLoader}>
                {conventions.length > 0 && (
                    <DataTile title={texts.CONVENTIONS} sx={documentsTileSx}>
                        {conventions.map(convention => (
                            <Box
                                key={convention.document.id}
                                display="flex"
                                flexWrap="wrap"
                                width="100%"
                            >
                                <Document
                                    fullName={`${convention.document.name.replace(
                                        /-\d{12}-\d+-[0-9a-fA-F]+\.[0-9a-fA-F]+|-[0-9a-fA-F]+\.[0-9a-fA-F]+/,
                                        ''
                                    )} ${convention.investmentFund?.name}`}
                                    doc={convention.document}
                                    onChange={handleDocumentChange}
                                />
                            </Box>
                        ))}
                    </DataTile>
                )}
            </Loader>
            <Loader loading={loading} marginTop={10} forceStop={!loading}>
                <DataTile title={texts.INSCRIPTION_DOCS} sx={documentsTileSx}>
                    {commonDocs?.map(doc => (
                        <Box key={doc.id} display="flex" flexWrap="wrap" width="100%">
                            <Document
                                doc={doc}
                                onChange={handleDocumentChange}
                                canArchive={userIsAdminDistributor && doc.type !== docTypes.OTHER}
                            />
                        </Box>
                    ))}
                    <UploadDocument userId={internalUser?.id} hideSelect />
                    <Box pt={3} width="100%">
                        <DocumentUpload
                            inline
                            type="image"
                            id="distributor-logo"
                            width="100%"
                            label={texts.COMPANY_LOGO}
                            ContainerProps={{ marginTop: 0, marginBottom: '10px' }}
                            selectedFiles={logoFile ? [logoFile] : []}
                            handleFileChange={handleLogoChange}
                            removeFile={handleRemoveLogo}
                        />
                        <ActionButton
                            disabled={!logoFile || createLoading || replaceLoading}
                            loading={createLoading || replaceLoading}
                            variant="contained"
                            onClick={handleLogoUpload}
                        >
                            {texts.SAVE}
                        </ActionButton>
                    </Box>
                </DataTile>
            </Loader>
            {!!documentAudits.length && (
                <Loader loading={documentAuditsLoader} marginTop={10}>
                    <DataTile title={texts.OLD_DOCUMENTS} sx={documentsTileSx}>
                        {documentAudits.map(doc => (
                            <Box key={doc.id} display="flex" flexWrap="wrap" width="100%">
                                <Document doc={doc} isAudit />
                            </Box>
                        ))}
                    </DataTile>
                </Loader>
            )}
        </ListContainer>
    );
};

export default DistributorDocuments;
